import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'sonner';

const ProcessingContext = createContext(null);

export function ProcessingProvider({ children }) {
  const [backgroundSession, setBackgroundSession] = useState(null);
  const [backgroundProgress, setBackgroundProgress] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pollIntervalRef = useRef(null);

  const startBackgroundPolling = useCallback((sessionId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/status`);
        const data = response.data;
        
        if (data.progress) {
          setBackgroundProgress(data.progress);
        } else {
          setBackgroundProgress(prev => ({
            phase: data.status,
            message: prev?.message || 'Processing...',
            percent: data.status === 'complete' ? 100 : (prev?.percent || 0),
            sub_progress: prev?.sub_progress || 0
          }));
        }
        
        if (data.status === 'complete' || data.status === 'error' || data.status === 'cancelled') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          
          if (data.status === 'complete') {
            toast.success('Your karaoke track is ready - time to sing!');
          } else if (data.status === 'error') {
            toast.error(data.error_message || 'Processing failed. Please try again.');
          } else if (data.status === 'cancelled') {
            toast.info('Processing was cancelled.');
          }
          
          setTimeout(() => {
            setBackgroundSession(null);
            setBackgroundProgress(null);
          }, 3000);
        }
      } catch (error) {
        console.error('Error polling background status:', error);
      }
    };

    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, 2000);
  }, []);

  const moveToBackground = useCallback((sessionId) => {
    setBackgroundSession(sessionId);
    setIsModalOpen(false);
    startBackgroundPolling(sessionId);
  }, [startBackgroundPolling]);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const clearBackground = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setBackgroundSession(null);
    setBackgroundProgress(null);
  }, []);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  return (
    <ProcessingContext.Provider value={{
      backgroundSession,
      backgroundProgress,
      isModalOpen,
      moveToBackground,
      openModal,
      closeModal,
      clearBackground
    }}>
      {children}
    </ProcessingContext.Provider>
  );
}

export function useProcessing() {
  const context = useContext(ProcessingContext);
  if (!context) {
    throw new Error('useProcessing must be used within a ProcessingProvider');
  }
  return context;
}
